import React from 'react';
import { Badge } from '@components/atoms/Badge';
import { SubcontractorHealth } from '@models/subcontractor.model';
import { useTranslation } from '@hooks/useTranslation';

interface HealthPillProps {
  health: SubcontractorHealth;
  size?: 'sm' | 'md';
  /**
   * Sprint 44. `subcontractor` (default) keeps the original labels; `client` uses the plainer
   * `clientProgress.health*` wording ("Nothing delegated" means nothing to a homeowner).
   */
  context?: 'subcontractor' | 'client';
}

const VARIANT_BY_HEALTH: Record<
  SubcontractorHealth,
  'success' | 'warning' | 'error' | 'secondary' | 'default'
> = {
  [SubcontractorHealth.ON_TRACK]: 'success',
  [SubcontractorHealth.BEHIND]: 'error',
  [SubcontractorHealth.NOT_STARTED]: 'secondary',
  [SubcontractorHealth.COMPLETE]: 'success',
  [SubcontractorHealth.NONE]: 'default',
};

export const HealthPill: React.FC<HealthPillProps> = ({
  health,
  size = 'sm',
  context = 'subcontractor',
}) => {
  const tSub = useTranslation('subcontractors');
  const tClient = useTranslation('clientProgress');
  const subLabels: Record<SubcontractorHealth, string> = {
    [SubcontractorHealth.ON_TRACK]: tSub?.healthOnTrack || 'On track',
    [SubcontractorHealth.BEHIND]: tSub?.healthBehind || 'Behind',
    [SubcontractorHealth.NOT_STARTED]: tSub?.healthNotStarted || 'Not started',
    [SubcontractorHealth.COMPLETE]: tSub?.healthComplete || 'All complete',
    [SubcontractorHealth.NONE]: tSub?.healthNone || 'Nothing delegated',
  };
  const clientLabels: Record<SubcontractorHealth, string> = {
    [SubcontractorHealth.ON_TRACK]: tClient?.healthOnTrack || 'On track',
    [SubcontractorHealth.BEHIND]: tClient?.healthBehind || 'Behind schedule',
    [SubcontractorHealth.NOT_STARTED]:
      tClient?.healthNotStarted || 'Not started',
    [SubcontractorHealth.COMPLETE]: tClient?.healthComplete || 'Complete',
    [SubcontractorHealth.NONE]: tClient?.healthNone || 'No tasks yet',
  };
  const labelByHealth = context === 'client' ? clientLabels : subLabels;

  return (
    <Badge size={size} variant={VARIANT_BY_HEALTH[health]}>
      {labelByHealth[health]}
    </Badge>
  );
};

export default HealthPill;
